/**
 * Navigation Handlers (Back/Forward)
 */

import { useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { markProgrammaticNavigation, bumpNavSerial } from "@/hooks/use-url-sync"
import type { OpenTab } from "../../features/document-tabs"
import type { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import { fetchWithAuth } from "@/lib/api"
import { devLog } from "@/lib/dev-log"
import { postCursorNavigationIngest } from "@/lib/nav-debug"
import { pathToUrl, buildAppUrl, type WorkspaceRouteInfo } from "@/lib/app-routes"
import { buildSearchHistoryUrl } from "@/lib/search/search-query-history"
import {
  isEditHistoryTabId,
  SEARCH_VIEW_TAB_ID,
  isSearchViewTabId,
} from "@/lib/static-app-views"
import { pinHomeTabFirstInOpenTabs } from "./shared"
import {
  parseBasecampTabId,
  resolveBasecampTabFromShorthandPath,
  STATIC_BASECAMP_TAB_META,
  type BasecampStaticTabMeta,
} from "@/lib/basecamp-tab-routing"
import type { NavigationEntry } from "@/hooks/use-navigation-history"
import {
  announceNextcloudHistorySelection,
  applyNextcloudHistoryEntry,
  NEXTCLOUD_SHELL_HISTORY_EVENT,
} from "@/lib/nextcloud-integration-nav"
import {
  isPeopleIndexTabId,
  isPersonSurfaceTabId,
  personUserIdFromTabId,
} from "@/lib/person-view"

/** API `/api/documents/...` JSON may expose path fields in several shapes; normalize for sidebar + workspace resolution. */
function documentPathFromApiResponse(docData: Record<string, unknown>): string | null {
  const candidates = ["file_path", "filePath", "path", "relative_path"] as const
  for (const k of candidates) {
    const v = docData[k]
    if (typeof v === "string" && v.length > 0) return v
  }
  return null
}

/** When history restores via API fetch (tab id no longer matched entry id), preserve workspace + linkage fields */
function mergeHistoricalDocumentOntoTab(
  tab: OpenTab,
  documentIdFromHistory: string,
  docData: Record<string, unknown>,
  getWorkspaceIdForPath?: (path: string) => string | undefined
): OpenTab {
  const fromApi = documentPathFromApiResponse(docData)

  let resolvedPath: string | undefined
  if (fromApi && fromApi.length > 0) {
    resolvedPath = fromApi
  } else if (documentIdFromHistory.includes("/")) {
    resolvedPath = documentIdFromHistory
  }

  let workspaceId = tab.workspaceId
  if (workspaceId == null && resolvedPath != null && getWorkspaceIdForPath) {
    workspaceId = getWorkspaceIdForPath(resolvedPath)
  }

  const title = typeof docData.title === "string" ? docData.title : tab.title
  const type = typeof docData.type === "string" ? docData.type : tab.type

  const { filePath: _ignoredFilePath, ...tabRest } = tab
  const next: OpenTab = {
    ...tabRest,
    id: documentIdFromHistory,
    title,
    type: type as OpenTab["type"],
    isDirty: false,
    tabSlotId: tab.tabSlotId,
  }

  if (resolvedPath !== undefined && resolvedPath.length > 0) {
    next.filePath = resolvedPath
  }

  if (workspaceId !== undefined && workspaceId !== null) {
    next.workspaceId = workspaceId
  }

  return next
}

type FolderRestoreBranch = "slot_explorer" | "slot_replace_doc" | "legacy_first_explorer" | "legacy_new_list"

/**
 * Map a folder history entry onto open tabs using the slot that owns the stack (activeSlotId).
 * Avoids activating the first global `list` tab when history belongs to another tab row.
 */
function applyHistoryFolderToOpenTabs(
  openTabs: OpenTab[],
  activeSlotId: string,
  folderPath: string,
  workspaceId: string | undefined
): { tabs: OpenTab[]; activeTabId: string; restoreBranch: FolderRestoreBranch } {
  const slotOwner = openTabs.find((t) => t.tabSlotId === activeSlotId)

  if (slotOwner?.type === "explorer") {
    return {
      restoreBranch: "slot_explorer",
      tabs: openTabs.map((tab) =>
        tab.tabSlotId === activeSlotId
          ? {
              ...tab,
              explorerData: { folderPath },
              ...(workspaceId != null ? { workspaceId } : {}),
            }
          : tab
      ),
      activeTabId: slotOwner.id,
    }
  }

  if (slotOwner) {
    const ownerId = slotOwner.id
    const mergedWorkspaceId =
      workspaceId ?? (slotOwner.workspaceId != null ? slotOwner.workspaceId : undefined)
    return {
      restoreBranch: "slot_replace_doc",
      tabs: openTabs
        .filter((tab) => tab.id !== "list" || tab.id === ownerId)
        .map((tab) =>
          tab.tabSlotId === activeSlotId
            ? {
                id: "list",
                title: "Explorer",
                type: "explorer" as const,
                isDirty: false,
                tabSlotId: activeSlotId,
                ...(slotOwner.isPinned !== undefined ? { isPinned: slotOwner.isPinned } : {}),
                explorerData: { folderPath },
                ...(mergedWorkspaceId != null ? { workspaceId: mergedWorkspaceId } : {}),
              }
            : tab
        ),
      activeTabId: "list",
    }
  }

  const explorerTab = openTabs.find((tab) => tab.id === "list" || tab.type === "explorer")
  const explorerTabId = explorerTab?.id || "list"

  if (!explorerTab) {
    return {
      restoreBranch: "legacy_new_list",
      tabs: [
        {
          id: "list",
          title: "Explorer",
          type: "explorer",
          isDirty: false,
          tabSlotId: "tab-main",
          explorerData: { folderPath },
          ...(workspaceId != null ? { workspaceId } : {}),
        },
        ...openTabs,
      ],
      activeTabId: explorerTabId,
    }
  }

  return {
    restoreBranch: "legacy_first_explorer",
    // Match by the specific explorerTab's own id (not by type), otherwise this would
    // overwrite explorerData on EVERY open Explorer tab (e.g. one per workspace
    // group), causing folder content to bleed across unrelated tabs.
    tabs: openTabs.map((tab) =>
      tab.id === explorerTab.id
        ? { ...tab, explorerData: { folderPath }, ...(workspaceId != null ? { workspaceId } : {}) }
        : tab
    ),
    activeTabId: explorerTabId,
  }
}

function restoreStaticBasecampTab(
  openTabs: OpenTab[],
  activeTabId: string | null,
  tabId: string,
  meta: BasecampStaticTabMeta,
  organizationId?: string,
  workspaceId?: string,
): OpenTab[] {
  const integrationData = {
    integrationId: 'basecamp',
    ...(meta.section && { section: meta.section }),
    ...(organizationId && { organizationId }),
  }
  return openTabs.map((tab) =>
    tab.id === activeTabId
      ? {
          id: tabId,
          title: meta.title,
          type: meta.type,
          isDirty: false,
          tabSlotId: tab.tabSlotId,
          integrationData,
          ...(workspaceId != null ? { workspaceId } : {}),
        }
      : tab
  )
}

function restorePersonSurfaceHistoryEntry(args: {
  openTabs: OpenTab[]
  activeTabId: string | null
  activeSlotId: string
  entry: NavigationEntry
  currentTab: OpenTab | undefined
}): { tabs: OpenTab[]; nextActiveTabId: string } | null {
  const documentId = args.entry.documentId
  if (!documentId || !isPersonSurfaceTabId(documentId)) return null
  const isIndex = isPeopleIndexTabId(documentId)
  const userId = personUserIdFromTabId(documentId)
  if (!isIndex && !userId) return null
  const workspaceId = args.entry.organizationId ?? args.currentTab?.workspaceId
  const next: OpenTab = {
    id: documentId,
    title: isIndex ? 'People' : (args.entry.title?.trim() || 'Person'),
    type: 'person-view',
    isDirty: false,
    tabSlotId: args.activeSlotId,
    ...(isIndex ? {} : { personUserId: userId! }),
    ...(workspaceId ? { workspaceId } : {}),
  }
  const withActive = args.openTabs.some((tab) => tab.id === args.activeTabId)
    ? args.openTabs.map((tab) => (tab.id === args.activeTabId ? next : tab))
    : [...args.openTabs, next]
  const tabs = withActive.filter((tab) => tab.type !== 'person-view' || tab.id === next.id)
  return { tabs, nextActiveTabId: documentId }
}

function restoreInboxThreadHistoryEntry(args: {
  openTabs: OpenTab[]
  activeTabId: string | null
  activeSlotId: string
  entry: NavigationEntry
  currentTab: OpenTab | undefined
}): { tabs: OpenTab[]; nextActiveTabId: string } | null {
  const documentId = args.entry.documentId
  if (!documentId) return null
  const isThread =
    args.entry.tabType === 'inbox-thread' || documentId.startsWith('inbox-thread-')
  if (!isThread) return null
  const threadId = documentId.replace(/^inbox-thread-/, '')
  if (!threadId) return null
  const next: OpenTab = {
    id: documentId,
    title: args.entry.title?.trim() || 'Inbox thread',
    type: 'inbox-thread',
    isDirty: false,
    tabSlotId: args.activeSlotId,
    inboxThreadId: threadId,
    ...(args.currentTab?.workspaceId ? { workspaceId: args.currentTab.workspaceId } : {}),
  }
  const tabs = args.openTabs.some((tab) => tab.id === args.activeTabId)
    ? args.openTabs.map((tab) => (tab.id === args.activeTabId ? next : tab))
    : [...args.openTabs, next]
  return { tabs, nextActiveTabId: documentId }
}

function syncUrlForRestoredTab(
  tab: OpenTab | undefined,
  workspacesRoute: WorkspaceRouteInfo[],
  navigate: ReturnType<typeof useNavigate>,
) {
  if (!tab) return
  navigate(buildAppUrl({ tab, workspaces: workspacesRoute }), { replace: true })
}

/**
 * Restore a Basecamp integration tab from a history entry.
 * Returns null when the id is not a Basecamp integration surface (caller loads as document).
 * `basecamp-todo-list-*` must be handled before any `basecamp-todo-*` prefix match.
 */
function restoreBasecampHistoryEntry(args: {
  openTabs: OpenTab[]
  activeTabId: string | null
  documentId: string
  entry: NavigationEntry
  currentTab: OpenTab | undefined
}): { tabs: OpenTab[]; nextActiveTabId: string } | null {
  const { openTabs, activeTabId, documentId, entry, currentTab } = args
  const parsed = parseBasecampTabId(documentId)
  if (!parsed) return null

  const organizationId =
    entry.organizationId ??
    currentTab?.integrationData?.organizationId ??
    currentTab?.workspaceId ??
    undefined

  if (parsed.kind === 'static') {
    return {
      tabs: restoreStaticBasecampTab(
        openTabs,
        activeTabId,
        parsed.tabId,
        parsed.meta,
        organizationId,
        organizationId ?? currentTab?.workspaceId,
      ),
      nextActiveTabId: parsed.tabId,
    }
  }

  if (parsed.kind === 'project') {
    const tabs = openTabs.map((tab) =>
      tab.id === activeTabId
        ? {
            id: parsed.tabId,
            title: currentTab?.integrationData?.projectName || 'Loading...',
            type: 'integration-basecamp' as const,
            isDirty: false,
            tabSlotId: tab.tabSlotId,
            integrationData: {
              integrationId: 'basecamp',
              projectId: parsed.projectId,
              ...(organizationId ? { organizationId } : {}),
            },
            ...(organizationId != null ? { workspaceId: organizationId } : {}),
          }
        : tab,
    )
    return { tabs, nextActiveTabId: parsed.tabId }
  }

  if (parsed.kind === 'todo-list') {
    const projectId =
      entry.projectId ?? currentTab?.integrationData?.projectId ?? null
    if (!projectId) {
      console.error(
        '[Navigation] Cannot restore Basecamp todo list - missing projectId',
        { documentId, entry },
      )
      return null
    }
    const listId = entry.listId ?? parsed.listId
    const tabs = openTabs.map((tab) =>
      tab.id === activeTabId
        ? {
            id: parsed.tabId,
            title:
              currentTab?.integrationData?.listName ||
              entry.listId ||
              'Loading list...',
            type: 'integration-basecamp-todo-list' as const,
            isDirty: false,
            tabSlotId: tab.tabSlotId,
            integrationData: {
              integrationId: 'basecamp',
              projectId,
              listId,
              ...(organizationId ? { organizationId } : {}),
            },
            ...(organizationId != null ? { workspaceId: organizationId } : {}),
          }
        : tab,
    )
    return { tabs, nextActiveTabId: parsed.tabId }
  }

  if (parsed.kind === 'todo') {
    const projectId =
      entry.projectId ?? currentTab?.integrationData?.projectId ?? null
    if (!projectId) {
      console.error(
        '[Navigation] Cannot restore Basecamp todo - missing projectId',
        { documentId, entry },
      )
      return null
    }
    const todoId = entry.todoId ?? parsed.todoId
    const tabs = openTabs.map((tab) =>
      tab.id === activeTabId
        ? {
            id: parsed.tabId,
            title: 'Loading...',
            type: 'integration-basecamp-todo' as const,
            isDirty: false,
            tabSlotId: tab.tabSlotId,
            integrationData: {
              integrationId: 'basecamp',
              projectId,
              todoId,
              ...(organizationId ? { organizationId } : {}),
            },
            ...(organizationId != null ? { workspaceId: organizationId } : {}),
          }
        : tab,
    )
    return { tabs, nextActiveTabId: parsed.tabId }
  }

  // kind === 'other' (e.g. bare basecamp) - use static meta if present
  const staticMeta = STATIC_BASECAMP_TAB_META[documentId]
  if (staticMeta) {
    return {
      tabs: restoreStaticBasecampTab(
        openTabs,
        activeTabId,
        documentId,
        staticMeta,
        organizationId,
        organizationId ?? currentTab?.workspaceId,
      ),
      nextActiveTabId: documentId,
    }
  }

  const tabs = openTabs.map((tab) =>
    tab.id === activeTabId
      ? {
          id: documentId,
          title:
            documentId === 'basecamp'
              ? 'Basecamp'
              : documentId.replace('basecamp-', '').replace(/-/g, ' '),
          type: 'integration-basecamp' as const,
          isDirty: false,
          tabSlotId: tab.tabSlotId,
          integrationData: {
            integrationId: 'basecamp',
            ...(entry.projectId || currentTab?.integrationData?.projectId
              ? {
                  projectId:
                    entry.projectId ?? currentTab?.integrationData?.projectId,
                }
              : {}),
            ...(organizationId ? { organizationId } : {}),
          },
          ...(organizationId != null ? { workspaceId: organizationId } : {}),
        }
      : tab,
  )
  return { tabs, nextActiveTabId: documentId }
}

function restoreSearchViewTab(
  openTabs: OpenTab[],
  activeTabId: string | null,
  activeSlotId: string,
  searchQuery?: string,
  searchPath?: string,
): { tabs: OpenTab[]; nextActiveTabId: string } {
  const path = (searchPath || '').trim() || undefined
  const tabs = openTabs.map((tab) =>
    tab.id === activeTabId
      ? {
          ...tab,
          id: SEARCH_VIEW_TAB_ID,
          title: tab.searchNamedView === 'activity' ? 'Activity' : 'Search',
          type: 'search-view' as const,
          isDirty: false,
          tabSlotId: activeSlotId,
          initialQuery: searchQuery ?? '',
          searchPath: path,
        }
      : tab,
  )
  return { tabs, nextActiveTabId: SEARCH_VIEW_TAB_ID }
}

export function useNavigationHandlers(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  setSelectedProjectId: (id: string | null) => void,
  setActiveDocumentPath: (path: string | null) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  navigate: ReturnType<typeof useNavigate>,
  getWorkspaceIdForPath?: (path: string) => string | undefined,
  workspacesRoute: WorkspaceRouteInfo[] = [],
) {
  const resolveExplorerWorkspace = (folderPath: string | null | undefined): string | undefined => {
    if (!folderPath || !getWorkspaceIdForPath) return undefined
    return getWorkspaceIdForPath(folderPath)
  }

  /**
   * Handle back navigation
   */
  const handleGoBack = useCallback(async () => {
    markProgrammaticNavigation();
    bumpNavSerial(); // invalidate any in-flight handleDocumentClick fetch

    const activeTab = openTabs.find(tab => tab.id === activeTabId);
    const activeSlotId = activeTab?.tabSlotId || 'tab-main';

    // ?? DEBUG NAVIGATION ??????????????????????????????????????????????????????
    const navState = navigation.tabNavigationState[activeSlotId];
    devLog('[NavBack] ? Back pressed', {
      activeTabId,
      activeSlotId,
      openTabIds: openTabs.map(t => t.id),
      historyLength: navState?.history.length ?? 0,
      historyIndex: navState?.currentIndex ?? -1,
      history: navState?.history.map(e => ({ docId: e.documentId, folderPath: e.folderPath })) ?? [],
    });
    // ??????????????????????????????????????????????????????????????????????????

    const entry = navigation.goBack(activeSlotId, "back-button");
    if (!entry) {
      devLog('[NavBack] goBack() returned null � already at start of history');
      // #region agent log
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H4_restore",
        location: "navigation-handlers.ts:handleGoBack",
        message: "goBack_null",
        data: { activeTabId, activeSlotId, historyLen: navState?.history.length ?? 0 },
        timestamp: Date.now(),
      })
      // #endregion
      return;
    }

    devLog('[NavBack] goBack() entry:', { documentId: entry.documentId, folderPath: entry.folderPath, viewMode: entry.viewMode });

    // #region agent log
    {
      const branch =
        entry.folderPath !== undefined && entry.folderPath !== null
          ? "explorer_folder"
          : entry.documentId
            ? "document"
            : "unknown"
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H4_restore",
        location: "navigation-handlers.ts:handleGoBack",
        message: "goBack_entry",
        data: {
          activeTabId,
          activeSlotId,
          branch,
          documentId: entry.documentId,
          folderPath: entry.folderPath ?? null,
          viewMode: entry.viewMode,
        },
        timestamp: Date.now(),
      })
    }
    // #endregion

    // Handle Explorer navigation (folderPath explicitly set to a non-null value)
    // Note: null check is intentional � folderPath: null (from initial history entry) must NOT trigger Explorer
    if (entry.folderPath !== undefined && entry.folderPath !== null) {
      const folderPath = entry.folderPath;
      const wsId = resolveExplorerWorkspace(folderPath)

      const { tabs, activeTabId: nextActiveTabId, restoreBranch } = applyHistoryFolderToOpenTabs(
        openTabs,
        activeSlotId,
        folderPath,
        wsId
      )
      setOpenTabs(tabs)
      setActiveTabId(nextActiveTabId)
      setSelectedProjectId(folderPath)

      // #region agent log
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H5_folder_restore",
        location: "navigation-handlers.ts:handleGoBack",
        message: "folder_restore_applied",
        data: { activeSlotId, restoreBranch, nextActiveTabId, folderPath },
        timestamp: Date.now(),
      })
      // #endregion

      const targetUrl = folderPath ? pathToUrl(folderPath) : '/';
      navigate(targetUrl, { replace: true });
      return;
    }

    // Restore global Home (documentId null, list viewMode)
    if (!entry.documentId && entry.viewMode === 'list') {
      const homeTabId = 'home'
      const homeTab: OpenTab = {
        id: homeTabId,
        title: 'Home',
        type: 'home',
        isDirty: false,
        tabSlotId: activeSlotId,
      }
      setOpenTabs((prev) =>
        pinHomeTabFirstInOpenTabs(
          prev.some((t) => t.id === homeTabId || t.type === 'home') ? prev : [...prev, homeTab],
        ),
      )
      setActiveTabId(homeTabId)
      setActiveDocumentPath(null)
      setSelectedProjectId(null)
      markProgrammaticNavigation()
      navigate('/', { replace: true })
      navigation.navigate(activeSlotId, { documentId: null, viewMode: 'list' }, 'back-button-restore-home')
      return
    }

    // Legacy Edit History history entries ? Search Activity named view
    if (isEditHistoryTabId(entry.documentId)) {
      window.dispatchEvent(
        new CustomEvent('flownotes:open-search-view', {
          detail: { query: '', view: 'activity' },
        }),
      )
      return
    }

    // Restore advanced search view
    if (isSearchViewTabId(entry.documentId)) {
      const { tabs, nextActiveTabId } = restoreSearchViewTab(
        openTabs,
        activeTabId,
        activeSlotId,
        entry.searchQuery,
        entry.searchPath,
      )
      setOpenTabs(tabs)
      setActiveTabId(nextActiveTabId)
      setActiveDocumentPath(null)
      markProgrammaticNavigation()
      navigate(buildSearchHistoryUrl(entry.searchQuery, entry.searchPath), { replace: true })
      return
    }

    const nextcloudRestored = applyNextcloudHistoryEntry({
      openTabs,
      activeTabId,
      activeSlotId,
      entry,
    })
    if (nextcloudRestored) {
      setOpenTabs(nextcloudRestored.tabs)
      setActiveTabId(nextcloudRestored.nextActiveTabId)
      setActiveDocumentPath(null)
      announceNextcloudHistorySelection(entry)
      syncUrlForRestoredTab(
        nextcloudRestored.tabs.find((tab) => tab.id === nextcloudRestored.nextActiveTabId),
        workspacesRoute,
        navigate,
      )
      return
    }

    // Handle document navigation (documentId present)
    if (entry.documentId) {
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const personRestored = restorePersonSurfaceHistoryEntry({
        openTabs,
        activeTabId,
        activeSlotId,
        entry,
        currentTab,
      })
      if (personRestored) {
        setOpenTabs(personRestored.tabs)
        setActiveTabId(personRestored.nextActiveTabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          personRestored.tabs.find((tab) => tab.id === personRestored.nextActiveTabId),
          workspacesRoute,
          navigate,
        )
        return
      }
      const inboxRestored = restoreInboxThreadHistoryEntry({
        openTabs,
        activeTabId,
        activeSlotId,
        entry,
        currentTab,
      })
      if (inboxRestored) {
        setOpenTabs(inboxRestored.tabs)
        setActiveTabId(inboxRestored.nextActiveTabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          inboxRestored.tabs.find((t) => t.id === inboxRestored.nextActiveTabId),
          workspacesRoute,
          navigate,
        )
        return
      }
      const shorthand = resolveBasecampTabFromShorthandPath(entry.documentId, workspacesRoute)
      const effectiveDocumentId = shorthand?.tabId ?? entry.documentId

      const existingTab =
        openTabs.find((tab) => tab.id === effectiveDocumentId && tab.tabSlotId === activeSlotId) ??
        openTabs.find((tab) => tab.id === effectiveDocumentId)

      if (existingTab) {
        setActiveTabId(existingTab.id)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(existingTab, workspacesRoute, navigate)
      } else if (shorthand) {
        const org = currentTab?.integrationData?.organizationId ?? shorthand.workspaceId ?? undefined
        const updatedTabs = restoreStaticBasecampTab(
          openTabs,
          activeTabId,
          shorthand.tabId,
          shorthand.meta,
          org,
          shorthand.workspaceId ?? org,
        )
        setOpenTabs(updatedTabs)
        setActiveTabId(shorthand.tabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          updatedTabs.find((t) => t.id === shorthand.tabId),
          workspacesRoute,
          navigate,
        )
      } else {
        const restored = restoreBasecampHistoryEntry({
          openTabs,
          activeTabId,
          documentId: effectiveDocumentId,
          entry,
          currentTab,
        })
        if (restored) {
          setOpenTabs(restored.tabs)
          setActiveTabId(restored.nextActiveTabId)
          setActiveDocumentPath(null)
          syncUrlForRestoredTab(
            restored.tabs.find((t) => t.id === restored.nextActiveTabId),
            workspacesRoute,
            navigate,
          )
        } else {
        // Regular document - fetch from API
        try {
          const apiUrl = effectiveDocumentId.includes('/')
            ? `/api/documents/by-path?path=${encodeURIComponent(effectiveDocumentId)}`
            : `/api/documents/${effectiveDocumentId}`

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const docData = await response.json() as Record<string, unknown>
            const updatedTabs = openTabs.map((tab) => {
              if (tab.id !== activeTabId) return tab
              const next = mergeHistoricalDocumentOntoTab(
                tab,
                effectiveDocumentId,
                docData,
                getWorkspaceIdForPath,
              )
              devLog('[NavHistory] Historical doc restore merged tab linkage', {
                documentId: effectiveDocumentId,
                workspaceId: next.workspaceId,
                filePath: next.filePath,
              })
              return next
            })
            setOpenTabs(updatedTabs)
            setActiveTabId(effectiveDocumentId)
            const restoredTab = updatedTabs.find((t) => t.id === effectiveDocumentId)
            const pathForTree =
              restoredTab?.filePath ??
              (effectiveDocumentId.includes('/') ? effectiveDocumentId : null)
            if (pathForTree) {
              setActiveDocumentPath(pathForTree)
            }
            syncUrlForRestoredTab(restoredTab, workspacesRoute, navigate)
          }
        } catch (error) {
          console.error('[Navigation] Failed to load historical document:', error)
        }
        }
      }

      if (
        !shorthand &&
        !effectiveDocumentId.startsWith('basecamp') &&
        effectiveDocumentId.includes('/')
      ) {
        setActiveDocumentPath(effectiveDocumentId)
      }
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setSelectedProjectId, setActiveDocumentPath, navigation, navigate, getWorkspaceIdForPath, workspacesRoute])

  /**
   * Handle forward navigation
   */
  const handleGoForward = useCallback(async () => {
    markProgrammaticNavigation();
    bumpNavSerial(); // invalidate any in-flight handleDocumentClick fetch

    const activeTab = openTabs.find(tab => tab.id === activeTabId);
    const activeSlotId = activeTab?.tabSlotId || 'tab-main';
    const entry = navigation.goForward(activeSlotId, "forward-button");
    if (!entry) {
      // #region agent log
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H4_restore",
        location: "navigation-handlers.ts:handleGoForward",
        message: "goForward_null",
        data: { activeTabId, activeSlotId },
        timestamp: Date.now(),
      })
      // #endregion
      return;
    }

    // #region agent log
    {
      const branch =
        entry.folderPath !== undefined && entry.folderPath !== null
          ? "explorer_folder"
          : entry.documentId
            ? "document"
            : "unknown"
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H4_restore",
        location: "navigation-handlers.ts:handleGoForward",
        message: "goForward_entry",
        data: {
          activeTabId,
          activeSlotId,
          branch,
          documentId: entry.documentId,
          folderPath: entry.folderPath ?? null,
          viewMode: entry.viewMode,
        },
        timestamp: Date.now(),
      })
    }
    // #endregion

    // Handle Explorer navigation (folderPath explicitly set to a non-null value)
    // Note: null check is intentional � folderPath: null (from initial history entry) must NOT trigger Explorer
    if (entry.folderPath !== undefined && entry.folderPath !== null) {
      const folderPath = entry.folderPath;
      const wsId = resolveExplorerWorkspace(folderPath)

      const { tabs, activeTabId: nextActiveTabId, restoreBranch } = applyHistoryFolderToOpenTabs(
        openTabs,
        activeSlotId,
        folderPath,
        wsId
      )
      setOpenTabs(tabs)
      setActiveTabId(nextActiveTabId)
      setSelectedProjectId(folderPath)

      // #region agent log
      postCursorNavigationIngest({
        sessionId: "76985a",
        hypothesisId: "H5_folder_restore",
        location: "navigation-handlers.ts:handleGoForward",
        message: "folder_restore_applied",
        data: { activeSlotId, restoreBranch, nextActiveTabId, folderPath },
        timestamp: Date.now(),
      })
      // #endregion

      const targetUrl = folderPath ? pathToUrl(folderPath) : '/';
      navigate(targetUrl, { replace: true });
      return;
    }

    // Restore advanced search view
    if (isSearchViewTabId(entry.documentId)) {
      const { tabs, nextActiveTabId } = restoreSearchViewTab(
        openTabs,
        activeTabId,
        activeSlotId,
        entry.searchQuery,
        entry.searchPath,
      )
      setOpenTabs(tabs)
      setActiveTabId(nextActiveTabId)
      setActiveDocumentPath(null)
      markProgrammaticNavigation()
      navigate(buildSearchHistoryUrl(entry.searchQuery, entry.searchPath), { replace: true })
      return
    }

    const nextcloudRestored = applyNextcloudHistoryEntry({
      openTabs,
      activeTabId,
      activeSlotId,
      entry,
    })
    if (nextcloudRestored) {
      setOpenTabs(nextcloudRestored.tabs)
      setActiveTabId(nextcloudRestored.nextActiveTabId)
      setActiveDocumentPath(null)
      announceNextcloudHistorySelection(entry)
      syncUrlForRestoredTab(
        nextcloudRestored.tabs.find((tab) => tab.id === nextcloudRestored.nextActiveTabId),
        workspacesRoute,
        navigate,
      )
      return
    }

    // Handle document navigation (documentId present)
    if (entry.documentId) {
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const personRestored = restorePersonSurfaceHistoryEntry({
        openTabs,
        activeTabId,
        activeSlotId,
        entry,
        currentTab,
      })
      if (personRestored) {
        setOpenTabs(personRestored.tabs)
        setActiveTabId(personRestored.nextActiveTabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          personRestored.tabs.find((tab) => tab.id === personRestored.nextActiveTabId),
          workspacesRoute,
          navigate,
        )
        return
      }
      const inboxRestored = restoreInboxThreadHistoryEntry({
        openTabs,
        activeTabId,
        activeSlotId,
        entry,
        currentTab,
      })
      if (inboxRestored) {
        setOpenTabs(inboxRestored.tabs)
        setActiveTabId(inboxRestored.nextActiveTabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          inboxRestored.tabs.find((t) => t.id === inboxRestored.nextActiveTabId),
          workspacesRoute,
          navigate,
        )
        return
      }
      const shorthand = resolveBasecampTabFromShorthandPath(entry.documentId, workspacesRoute)
      const effectiveDocumentId = shorthand?.tabId ?? entry.documentId

      const existingTab =
        openTabs.find((tab) => tab.id === effectiveDocumentId && tab.tabSlotId === activeSlotId) ??
        openTabs.find((tab) => tab.id === effectiveDocumentId)

      if (existingTab) {
        setActiveTabId(existingTab.id)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(existingTab, workspacesRoute, navigate)
      } else if (shorthand) {
        const org = currentTab?.integrationData?.organizationId ?? shorthand.workspaceId ?? undefined
        const updatedTabs = restoreStaticBasecampTab(
          openTabs,
          activeTabId,
          shorthand.tabId,
          shorthand.meta,
          org,
          shorthand.workspaceId ?? org,
        )
        setOpenTabs(updatedTabs)
        setActiveTabId(shorthand.tabId)
        setActiveDocumentPath(null)
        syncUrlForRestoredTab(
          updatedTabs.find((t) => t.id === shorthand.tabId),
          workspacesRoute,
          navigate,
        )
      } else {
        const restored = restoreBasecampHistoryEntry({
          openTabs,
          activeTabId,
          documentId: effectiveDocumentId,
          entry,
          currentTab,
        })
        if (restored) {
          setOpenTabs(restored.tabs)
          setActiveTabId(restored.nextActiveTabId)
          setActiveDocumentPath(null)
          syncUrlForRestoredTab(
            restored.tabs.find((t) => t.id === restored.nextActiveTabId),
            workspacesRoute,
            navigate,
          )
        } else {
        try {
          const apiUrl = effectiveDocumentId.includes('/')
            ? `/api/documents/by-path?path=${encodeURIComponent(effectiveDocumentId)}`
            : `/api/documents/${effectiveDocumentId}`

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const docData = await response.json() as Record<string, unknown>
            const updatedTabs = openTabs.map((tab) => {
              if (tab.id !== activeTabId) return tab
              const next = mergeHistoricalDocumentOntoTab(
                tab,
                effectiveDocumentId,
                docData,
                getWorkspaceIdForPath,
              )
              devLog('[NavHistory] Historical doc restore merged tab linkage', {
                documentId: effectiveDocumentId,
                workspaceId: next.workspaceId,
                filePath: next.filePath,
              })
              return next
            })
            setOpenTabs(updatedTabs)
            setActiveTabId(effectiveDocumentId)
            const restoredTab = updatedTabs.find((t) => t.id === effectiveDocumentId)
            const pathForTree =
              restoredTab?.filePath ??
              (effectiveDocumentId.includes('/') ? effectiveDocumentId : null)
            if (pathForTree) {
              setActiveDocumentPath(pathForTree)
            }
            syncUrlForRestoredTab(restoredTab, workspacesRoute, navigate)
          }
        } catch (error) {
          console.error('[Navigation] Failed to load historical document:', error)
        }
        }
      }

      if (
        !shorthand &&
        !effectiveDocumentId.startsWith('basecamp') &&
        effectiveDocumentId.includes('/')
      ) {
        setActiveDocumentPath(effectiveDocumentId)
      }
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setSelectedProjectId, setActiveDocumentPath, navigation, navigate, getWorkspaceIdForPath, workspacesRoute])

  useEffect(() => {
    const onShellHistory = (event: Event) => {
      const direction = (event as CustomEvent<'back' | 'forward'>).detail
      if (direction === 'forward') void handleGoForward()
      else void handleGoBack()
    }
    window.addEventListener(NEXTCLOUD_SHELL_HISTORY_EVENT, onShellHistory)
    return () => window.removeEventListener(NEXTCLOUD_SHELL_HISTORY_EVENT, onShellHistory)
  }, [handleGoBack, handleGoForward])

  return {
    handleGoBack,
    handleGoForward,
  };
}
